import { HttpHeaders } from '@angular/common/http';
import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { FeatureFlag } from 'app/common/enums/feature-flag.enum';
import { isNullOrUndefined } from 'is-what';
import type { Subscription } from 'rxjs';
import { interval } from 'rxjs';

import { PassDataService } from '../../mega-exchange/services/pass-data.service';
import { StringConstants } from '../../rcr-exchange/constants/string-constants';
import { FmLoadingPageService } from '../../shared/services/fm-loading-page.service';
import { SessionService } from '../../shared/services/session.service';
import { UserService } from '../../shared/services/user.service';
import { XlsxToJsonService } from '../../shared/services/xlsx-to-json.service';
import type { CollateralDueMegaResponse } from '../services/interfaces/collateral-due-mega-response';
import { MegaTradeNavigationService } from '../services/mega-trade-navigation.service';
import { MegasService } from '../services/megas.service';
import { Upload } from './upload';

@Component({
  standalone: false,
  selector: 'invd-upload-pool-info',
  templateUrl: './upload-pool-info.component.html',
  styleUrls: ['./upload-pool-info.component.scss'],
})
export class UploadPoolInfoComponent implements OnInit, AfterViewInit, OnDestroy {
  public collateralDueRes: any;
  public collateralDueMegaList: any;
  public isInvalidFileFormat = false;
  errors: Array<string> = [];
  @Input() displayErrorMessage: string;
  public collateralDueErrorMsgOne = '';
  public collateralDueErrorMsgTwo = '';
  @Input() fileExt = 'xls, xlsx';
  @Output() uploadStatus = new EventEmitter();
  currentUpload: Upload;
  dropzoneActive = false;
  public isMultipleFiles = false;
  displayErrorMsgForMultipleFiles = '';
  showCancelBanner = false;
  displayCancelErrorMessage = '';
  showFileUploadScreen = true;
  files: any;
  timer: any;
  subscription: any;
  downloadXLSXResponse: any;
  showFileUploadFailBanner = false;
  displayFileUploadErrorMessage: string;
  navSubscription: Subscription;
  isAllowToNavigateAway = false;
  displayNavigateAwayPopup = false;
  destinationUrl = '/';
  public errorCounter = 0;
  isCancelLinkClicked = false;
  public featuretoggle: any;
  public LDshowCollateralUploadParseFile = false;
  collateralErrorMessage = '';
  public poolInfoTemplateClick = true;

  public excelSheetContent: any;
  public hasHeaderRow = true;
  public fileName = '';
  public sheets: string[] = [];
  public hasFileExtensionError = false;
  public isFileEmpty = false;
  public displayFileDataSelectionPopup = false;
  public isFileCorrupted = false;
  public showBannerErrorMsg = '';
  public isFilePasswordProcted = false;
  public showIEAnalyzerPopUp = false;

  private router = inject(Router);
  public fmLoadingPageService = inject(FmLoadingPageService);
  private userService = inject(UserService);
  private sessionService = inject(SessionService);
  private megaService = inject(MegasService);
  private passDataService = inject(PassDataService);
  private xlsxToJsonService = inject(XlsxToJsonService);
  private megaNavService = inject(MegaTradeNavigationService);
  private cdr = inject(ChangeDetectorRef);

  @HostListener('window:beforeunload', ['$event'])
  refreshBtnClick($event) {
    $event.returnValue = true;
  }

  ngOnInit() {
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    this.timer = interval(7000);
    this.navigationController();

    if (
      window.navigator.userAgent.includes('MSIE ') ||
      window.navigator.userAgent.includes('Trident/')
    ) {
      this.showIEAnalyzerPopUp = true;
    }

    this.isFileEmpty = false;
    this.poolInfoTemplateClick = true;
    if (this.userService && this.userService.isMEGAUser === true) {
      this.featuretoggle = this.userService.toggleFeatures;
      if (this.featuretoggle && this.featuretoggle.includes(FeatureFlag.COLLATERAL_UPLOAD_PARSED)) {
        this.LDshowCollateralUploadParseFile = true;
      }
    }
    this.getMegasPoolCollateralDue();
    if (this.passDataService.getIsUploadCancelledFromValidatePoolsPage()) {
      this.showCancelBanner = true;
      this.displayCancelErrorMessage = this.passDataService.getCancelErrorMessageFromValidatePool();
    }

    this.xlsxToJsonService.checkPasswordProtected.subscribe((message) => {
      if (message !== null) {
        this.isFilePasswordProcted = true;
        this.closeFileUploadMessageBanner();
        this.showBannerErrorMsg = StringConstants.ANALYZER_SHOW_PASSWORD_PROTECTED_FILE_ERROR;
      }
    });
  }

  ngAfterViewInit() {
    this.navigationController();
  }

  canDeactivate(): Promise<boolean> | boolean {
    return this.isAllowToNavigateAway;
  }

  public navigationController() {
    this.navSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.url.indexOf('validate-pools') > 0) {
          this.isAllowToNavigateAway = true;
          this.displayNavigateAwayPopup = false;
          this.destinationUrl = event.url;
        } else if (event.url.indexOf('error') > 0) {
          this.displayNavigateAwayPopup = false;
          if (this.subscription) {
            this.subscription.unsubscribe();
          }
          this.isAllowToNavigateAway = true;
        } else if (
          (!event.url.includes('upload-pool-info') && !this.showFileUploadScreen) ||
          this.errorCounter > 0
        ) {
          this.displayNavigateAwayPopup = true;
          if (this.subscription) {
            this.subscription.unsubscribe();
          }
          this.isAllowToNavigateAway = false;
          this.destinationUrl = event.url;
        } else {
          this.displayNavigateAwayPopup = false;
          this.isAllowToNavigateAway = true;
          this.destinationUrl = event.url;
        }
      }
      if (!(event instanceof NavigationEnd)) {
        return;
      }
      window.scrollTo(0, 0);
    });
    this.errorCounter = 0;
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.navSubscription) {
      this.navSubscription.unsubscribe();
    }
    this.passDataService.setIsUploadCancelledFromValidatePoolsPage(false);
  }

  public getMegasPoolCollateralDue(): void {
    this.megaService.megasPoolCollateralDue().subscribe({
      next: (data) =>
        this.handleMegasPoolCollateralDueMegaServiceSuccess(data as CollateralDueMegaResponse),
      error: (error) => this.handleAWSMegasPoolCollateralDueServiceFail(error),
    });
  }

  public handleMegasPoolCollateralDueMegaServiceSuccess(
    collateralDue: CollateralDueMegaResponse,
  ): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.collateralDueRes = collateralDue && collateralDue.collateralDueMegaList;
    this.collateralDueMegaList = this.collateralDueRes;
    this.collateralErrorMessage = collateralDue.errorMessage;
    this.cdr.markForCheck();
  }

  public handleAWSMegasPoolCollateralDueServiceFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public navigateToSettledMegas() {
    this.router.navigate(['mega-trade/settled-megas']);
  }

  public onPoolUpload(event): void {
    this.isInvalidFileFormat = false;
    this.isMultipleFiles = false;
    this.showCancelBanner = false;
    this.showFileUploadFailBanner = false;

    this.files = event.target.files;
    this.handlePoolInfoProcess(this.files);
    this.fmLoadingPageService.isLoaderRequesting.set(false);
  }

  public handlePoolInfoProcess(files): void {
    this.errors = [];
    if (files.length > 0 && !this.isValidFileExtension(files)) {
      this.uploadStatus.emit(false);
      return;
    }
    if (files.length > 0) {
      this.onFileUpload(files[0]);
    }
  }

  public includes(container, value) {
    let returnValue = false;
    const pos = container.indexOf(value);
    if (pos >= 0) {
      returnValue = true;
    }
    return returnValue;
  }

  public isValidFileExtension(files): boolean {
    const extensions = this.fileExt.split(',').map(function (x) {
      return x.toLocaleUpperCase().trim();
    });
    let exists = false;
    for (const file of files) {
      const ext = file.name.toUpperCase().split('.').pop() || file.name;
      exists = this.includes(extensions, ext);
      if (!exists) {
        this.isInvalidFileFormat = true;
        this.displayErrorMessage = StringConstants.MEGA_UPLOAD_POOL_FILE_FORMAT_TEXT;
      }
    }
    return exists;
  }

  dropzoneState(event: boolean) {
    this.dropzoneActive = event;
  }

  handleDrop(fileList: FileList) {
    this.isInvalidFileFormat = false;
    this.isMultipleFiles = false;
    this.showCancelBanner = false;
    this.showFileUploadFailBanner = false;
    if (fileList.length > 1) {
      this.isMultipleFiles = true;
      this.displayErrorMsgForMultipleFiles =
        StringConstants.MEGA_UPLOAD_POOL_MULTIPLE_FILES_ERROR_TEXT;
    } else {
      this.currentUpload = new Upload(fileList[0]);
      if (this.isValidFileExtension(fileList)) {
        this.onFileUpload(this.currentUpload.file);
      }
    }
  }

  handleDropParseFile(fileList: FileList) {
    this.isInvalidFileFormat = false;
    this.isMultipleFiles = false;
    this.showCancelBanner = false;
    this.showFileUploadFailBanner = false;
    if (fileList.length > 1) {
      this.isMultipleFiles = true;
      this.displayErrorMsgForMultipleFiles =
        StringConstants.MEGA_UPLOAD_POOL_MULTIPLE_FILES_ERROR_TEXT;
    } else {
      this.currentUpload = new Upload(fileList[0]);
      if (this.isValidFileExtension(fileList)) {
        this.getExcelSheetsContent({});
      }
    }
  }

  public closeErroMessageBanner(): void {
    this.isInvalidFileFormat = false;
    this.isMultipleFiles = false;
  }

  public onFileUpload(fileInput: any): void {
    this.isCancelLinkClicked = false;
    const fi = fileInput;
    this.showFileUploadScreen = false;
    if (fi && fi.name) {
      const fileName = fi.name;

      const request: any = new Object();
      request.userSupplementId = this.userService.getUserSupplementId();
      request.trainsientId = this.sessionService && this.sessionService.sessionId;
      request.fileName = fileName;

      this.passDataService.setCollateralUploadFileName(fileName);

      this.megaService.uploadLocation(request).subscribe({
        next: (data) => this.handelUploadCollateralSuccess(data, fileInput),
        error: (error) => this.handelUploadCollateralFail(error),
      });
    }
  }

  public callFileUpload(fileInput: any, url: string, kmsId: string) {
    const fi = fileInput;
    if (fi.name) {
      const fileToUpload = fi;
      const httpOptions = {
        headers: new HttpHeaders({
          'x-amz-server-side-encryption': 'aws:kms',
          'x-amz-server-side-encryption-aws-kms-key-id': kmsId,
        }),
      };

      this.megaService.uploadCollateral(fileToUpload, httpOptions, url).subscribe({
        next: (data) => this.handelUploadSuccess(data),
        error: (error) => this.handleUploadFail(error),
      });
    }
  }

  public handelUploadSuccess(_response: any) {
    this.showFileUploadScreen = false;
    this.fmLoadingPageService.isLoaderRequesting.set(false);

    this.subscription = this.timer.subscribe(() => {
      this.callUploadStatus();
    });
    this.cdr.markForCheck();
  }

  public handleUploadFail(error: any): void {
    if (error.status === 200) {
      this.showFileUploadScreen = false;
      this.fmLoadingPageService.isLoaderRequesting.set(false);
      this.errorCounter++;
      this.subscription = this.timer.subscribe(() => {
        this.callUploadStatus();
      });
    } else {
      this.showFileUploadFailBanner = true;
      this.displayFileUploadErrorMessage =
        'An unexpected error occured. Please try your upload again.';
      this.showFileUploadScreen = true;
      this.fmLoadingPageService.isLoaderRequesting.set(false);
    }
    this.cdr.markForCheck();
  }

  public handelUploadCollateralSuccess(response: any, fileInput: any) {
    const url = response && response.url;
    const kmsId = response && response.keyId;
    this.passDataService.setJobReferenceUUID(response.jobReferenceUuid);
    this.callFileUpload(fileInput, url, kmsId);
  }

  public handelUploadCollateralFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
    this.router.navigate(['error']);
  }

  public cancelUpload(): void {
    this.isCancelLinkClicked = true;
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    const request: any = new Object();
    request.jobReferenceUuid = this.passDataService.getJobReferenceUUID();

    this.megaService.cancelUpload(request).subscribe({
      next: (data) => this.handleCancelUploadSuccess(data),
      error: (error) => this.handleCancelUploadFail(error),
    });
  }

  public handleCancelUploadSuccess(response: any) {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.showCancelBanner = true;
    this.showFileUploadScreen = true;
    this.displayCancelErrorMessage = response.message;
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public handleCancelUploadFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public closeCancelMessageBanner(): void {
    this.showCancelBanner = false;
  }

  public callUploadStatus(): void {
    const request: any = new Object();
    request.jobReferenceUuid = this.passDataService.getJobReferenceUUID();
    if (!this.isCancelLinkClicked) {
      this.megaService.uploadStatus(request).subscribe({
        next: (data) => this.handleFileUploadStatusSuccess(data),
        error: (error) => this.handleFileUploadStatusFail(error),
      });
    }
  }

  public handleFileUploadStatusSuccess(response: any) {
    if (response && response.fileStatus === 'Passed' && !this.isCancelLinkClicked) {
      if (this.subscription) {
        this.subscription.unsubscribe();
      }
      this.passDataService.setValidatedMegaList(response);
      if (!this.displayNavigateAwayPopup) {
        this.megaNavService.canNavigate = true;
        this.router.navigate(['/mega-upload/upload-pool-info/validate-pools']);
      }
    } else if (response && response.fileStatus === 'Failed') {
      if (this.subscription) {
        this.subscription.unsubscribe();
      }
      this.showFileUploadFailBanner = true;
      this.showFileUploadScreen = true;
      this.displayFileUploadErrorMessage = response.errorMessage;
    } else if (response && response.fileStatus === 'Scanning') {
      this.showFileUploadScreen = false;
    }
    this.cdr.markForCheck();
  }

  public handleFileUploadStatusFail(_error: any): void {
    this.subscription.unsubscribe();
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public closeFileUploadMessageBanner(): void {
    this.showFileUploadFailBanner = false;
  }

  public downloadPoolInfoTemplate(): void {
    this.fmLoadingPageService.isLoaderRequesting.set(true);
    if (this.poolInfoTemplateClick) {
      this.megaService.downloadXLSXPoolInfoTemplate().subscribe({
        next: (data) => this.handleDownloadXLSXServiceSuccess(data),
        error: (error) => this.handleAWSownloadXLSXServiceFail(error),
      });
    }
    this.poolInfoTemplateClick = false;
  }

  public handleDownloadXLSXServiceSuccess(downloadXLSXRes) {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.downloadXLSXResponse = downloadXLSXRes;
    let data = this.downloadXLSXResponse;
    const fileName = 'Fannie Mae Pool Information Template.xlsx';
    data = data.replace(/^[^,]+,/, '');
    data = data.replace(/\s/g, '');
    const byteCharacters = window.atob(data);
    const byteNumbers = Array.from({ length: byteCharacters.length });
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers as any);
    const blob = new Blob([byteArray], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    if ((window.navigator as any).msSaveOrOpenBlob) {
      (window.navigator as any).msSaveOrOpenBlob(blob, fileName);
    } else {
      const objectUrl: string = URL.createObjectURL(blob);
      const a: HTMLAnchorElement = document.createElement('a') as HTMLAnchorElement;
      a.href = objectUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    }

    this.poolInfoTemplateClick = true;
    this.cdr.markForCheck();
  }

  public handleAWSownloadXLSXServiceFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.poolInfoTemplateClick = true;
    this.cdr.markForCheck();
  }

  public navigatePageTo(pageURL) {
    this.router.navigate([pageURL]);
    this.isAllowToNavigateAway = true;
    this.cancelUpload();
  }

  public continuePollingUploadStatus(): void {
    this.displayNavigateAwayPopup = false;
    this.callUploadStatus();
  }

  /*
   * Analyzer Upload
   */

  public showFileExtensionError(event) {
    this.hasFileExtensionError = event ? true : false;
    if (this.hasFileExtensionError) {
      this.closeFileUploadMessageBanner();
    }
    this.showBannerErrorMsg = this.hasFileExtensionError
      ? StringConstants.MEGA_UPLOAD_POOL_FILE_FORMAT_TEXT
      : '';
  }

  public getExcelSheetsContent(event) {
    this.closeMessageBanner();
    this.closeFileUploadMessageBanner();
    this.sheets = [];
    let req = [];
    const collateralDetailsReq = [];
    const reqMain: any = new Object();
    this.sheets = Object.keys(event.getExcelSheetsContent);
    this.excelSheetContent = event && event.getExcelSheetsContent;

    if (this.sheets) {
      this.sheets.forEach((sheetName) => {
        req = [];
        const reqTemp = {};

        if (this.excelSheetContent?.[sheetName]) {
          this.excelSheetContent[sheetName].sheet.forEach((item, index) => {
            const mBundle: any = {};
            if (index === 0) {
              mBundle.rowNumber = index + 1;
              mBundle.cusip = !isNullOrUndefined(item[0])
                ? /^[a-zA-Z0-9]+$/.test(item[0]) && item[0].length === 9
                  ? item[0]
                  : item[0] && item[0].toString()
                : null;
              mBundle.contribOrigFace = !isNullOrUndefined(item[1])
                ? /^[$]?([0-9]{1,3}?[,]?)*[0-9]{1,3}?[.]?[0-9]*?$/.test(item[1])
                  ? this.formatAmountCU(item[1])
                  : null
                : null;
              mBundle.isAddOrEdit = 'N';
              if (isNullOrUndefined(mBundle.cusip) && isNullOrUndefined(mBundle.contribOrigFace)) {
                req = req;
              } else {
                if (
                  mBundle.cusip &&
                  mBundle.cusip.length !== 9 &&
                  isNullOrUndefined(mBundle.contribOrigFace)
                ) {
                  req = req;
                } else {
                  req.push(mBundle);
                }
              }
            } else {
              mBundle.rowNumber = index + 1;
              mBundle.cusip = !isNullOrUndefined(item[0])
                ? /^[a-zA-Z0-9]+$/.test(item[0]) && item[0].length === 9
                  ? item[0]
                  : item[0] && item[0].toString()
                : null;
              mBundle.contribOrigFace = !isNullOrUndefined(item[1])
                ? /^[$]?([0-9]{1,3}?[,]?)*[0-9]{1,3}?[.]?[0-9]*?$/.test(item[1])
                  ? this.formatAmountCU(item[1])
                  : item[1]
                : null;
              mBundle.isAddOrEdit = 'N';
              if (isNullOrUndefined(mBundle.cusip) && isNullOrUndefined(mBundle.contribOrigFace)) {
                req = req;
              } else {
                req.push(mBundle);
              }
            }
          }, this);
        }

        reqTemp[sheetName] = req;
        collateralDetailsReq.push(reqTemp);
      }, this);
    }

    reqMain.userSupplementId = this.userService.getUserSupplementId();
    reqMain.trainsientId = this.sessionService && this.sessionService.sessionId;
    reqMain.fileName = this.fileName;
    reqMain.collateralDetails = collateralDetailsReq;

    this.getUploadCollateral(reqMain);
  }

  public formatAmountCU(amount): any {
    let formatAmount = 0;
    let amountWithPricision = '0.00';
    amount = amount.toString().replace('$', '').replace(/\,/g, '');
    amountWithPricision = parseFloat(amount).toFixed(2);
    formatAmount = parseInt(amountWithPricision, 10);
    return formatAmount;
  }

  public showIsFileCorrupted(event) {
    this.isFileCorrupted = event ? true : false;
    if (this.isFileCorrupted) {
      this.showBannerErrorMsg = StringConstants.ANALYZER_SHOW_CORRUPTED_FILE_ERROR;
    }
  }

  public getUploadCollateral(req: any): void {
    this.isCancelLinkClicked = false;
    this.showFileUploadScreen = false;
    this.megaService.uploadCollateralDetails(req).subscribe({
      next: (data) =>
        this.handleMegasPoolUploadCollateralServiceSuccess(data as CollateralDueMegaResponse),
      error: (error) => this.handleAWSMegasPoolUploadCollateralServiceFail(error),
    });
  }

  public handleMegasPoolUploadCollateralServiceSuccess(res: any): void {
    this.passDataService.setJobReferenceUUID(res.jobReferenceUuid);
    this.callUploadStatus();
    this.subscription = this.timer.subscribe(() => {
      this.callUploadStatus();
    });
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public handleAWSMegasPoolUploadCollateralServiceFail(_error: any): void {
    this.fmLoadingPageService.isLoaderRequesting.set(false);
    this.cdr.markForCheck();
  }

  public showFileName(event) {
    const fileN = event.fileName ? event.fileName : '';
    this.fileName = fileN;
    this.passDataService.setCollateralUploadFileName(this.fileName);
  }

  public closeMessageBanner(): void {
    this.hasFileExtensionError = false;
    this.isFileEmpty = false;
    this.isFileCorrupted = false;
    this.isFilePasswordProcted = false;
  }

  public navigateToHome(): void {
    this.router.navigate(['/']);
  }

  protected trackByIdentity(_index: number, obj: any) {
    return obj;
  }
}
